import { Link } from 'react-router-dom';
import { reputationPath } from '../../helpers/reputationPath';
import {
  DIMENSION_META,
  isPlaceholderReputation,
  vouchCaption,
} from '../../helpers/reputationView';
import { describeEffectiveScore } from '../../helpers/reputationVouchDisplay';
import DomainBadges from '../UserProfile/DomainBadges';
import { ReputationBar } from './ReputationBar';
import { ReputationGauge } from './ReputationGauge';
import './ReputationCard.scss';

interface ReputationData {
  source: string;
  overallScore?: number;
  vouchBoost?: number;
  domains?: string[];
  dimensionScores?: Record<string, number>;
  dimensionConfidences?: Record<string, number>;
  evidence?: Record<string, string[]>;
}

interface ReputationCardProps {
  username: string;
  reputation: ReputationData | null | undefined;
  /**
   * `full` (default) shows the score, domains, and the per-dimension breakdown
   * — used on the owner's own profile. `credential` shows only the score +
   * domains (no dimension bars): a restrained "credential, not leaderboard" read
   * for other users' public profiles (TRIL-346).
   */
  variant?: 'full' | 'credential';
}

export function ReputationCard({
  username,
  reputation,
  variant = 'full',
}: ReputationCardProps) {
  if (isPlaceholderReputation(reputation)) {
    return (
      <div className="reputation-card">
        <p>
          {variant === 'credential'
            ? 'No reputation yet — this author hasn’t published enough to be scored.'
            : 'Not enough activity yet to establish a reputation. Keep publishing to build it.'}
        </p>
      </div>
    );
  }

  const {
    overallScore = 0,
    vouchBoost = 0,
    domains = [],
    dimensionScores = {},
    dimensionConfidences = {},
  } = reputation!;

  const { effectiveShown, effectiveRaw, earnedShown, vouchedShown, hasBoost } =
    describeEffectiveScore(overallScore, vouchBoost);

  if (variant === 'credential') {
    return (
      <div className="reputation-card reputation-card--credential">
        <ReputationGauge score={effectiveRaw} />
        {hasBoost && (
          <p className="reputation-card__vouch-caption">
            {vouchCaption(earnedShown, vouchedShown)}
          </p>
        )}
        <p className="reputation-card__caption">Reputation</p>
        <DomainBadges domains={domains} />
      </div>
    );
  }

  return (
    <div className="reputation-card">
      <ReputationGauge score={effectiveRaw} />
      {hasBoost && (
        <p className="reputation-card__vouch-caption">
          {vouchCaption(earnedShown, vouchedShown)}
        </p>
      )}
      <DomainBadges domains={domains} />
      {DIMENSION_META.map((d) => (
        <ReputationBar
          key={d.key}
          label={d.label}
          score={dimensionScores[d.key] ?? 0}
          confidence={dimensionConfidences[d.key] ?? 0}
        />
      ))}
      <Link to={reputationPath(username)}>See full breakdown →</Link>
    </div>
  );
}
